import MatchStars from './MatchStars';
import { talentLevel } from '../../utils/matchRank';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, } from 'react-router-dom/cjs/react-router-dom.min';
import { useGetAllMatchedTalentPerJobReqQuery, useGetLocationTabsQuery, useLazyGetAHireRequestByIdQuery } from '../../reactWrapper/Redux/apiSlice';
import Pagination from '../Pagination/Pagination';
import Table from './Table';
import { useRematchMutation } from '../../reactWrapper/endpoints/talent/talent.api';
import Button from '../Button';
import ButtonSpinner from '../Loader/ButtonSpinner';
import Status from '../Status/Status';
import { useParams } from 'react-router-dom';
//import { MatchedTalentsList } from '../../mockData';

const stackName = (user) => {
  const stack = user?.userCbtStack?.stack1;
  if (stack && typeof stack === 'object') return stack.name ?? '';
  return user?.userCbtStack?.stackName ?? user?.primaryStack ?? '';
};

/**
 * How a talent met the job, in one short label. Only the cases worth saying
 * out loud get a badge -- doing the main role as your main role is the
 * ordinary case, and labelling it is noise on every row.
 */
const roleBadge = (row) => {
  const as = row?.matched_as ?? 'primary';
  const via = row?.matched_via ?? 'primary';
  if (as === 'both') return { label: 'Both roles', className: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
  if (as === 'secondary') return { label: 'Not the main role', className: 'bg-amber-50 text-amber-800 border-amber-200' };
  if (via === 'secondary') return { label: 'Via their 2nd role', className: 'bg-slate-50 text-slate-600 border-slate-200' };
  return null;
};
const MatchedTalentsTable = ({ jobId: propJobId }) => {
  const params = useParams();
  const paramJobId = params?.jobId;
  const jobId = propJobId || paramJobId
  const siblingCount = 1;
  const pageColumn = 1;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  // Location ring tabs (gated jobs only): "inside" is the match, the rest
  // are qualified talents outside the job's stated location.
  const [ring, setRing] = useState('inside');
  const { data: locationTabs, refetch: refetchTabs } = useGetLocationTabsQuery(jobId, { skip: !jobId });
  const { data, refetch } = useGetAllMatchedTalentPerJobReqQuery({ reqId: jobId, page, ring })
  const [fetchJob] = useLazyGetAHireRequestByIdQuery();
  // True from the rematch request until the API has written the real
  // matching_info (the event runs in the background after "Rematch done").
  const [isMatching, setIsMatching] = useState(false);
  const { rematchTalents } = useRematchMutation(jobId)

  const handlePageChange = useCallback(
    (page) => {
      setPage(page - 1);
    },
    [page]
  );

  const tableHeader = useMemo(
    () => [
      {
        title: 'Talents',
        key: 'talent',
      },
      /* How much of the brief each person answered: the list is one pool
         but the people in it reached different depths of the funnel. */
      {
        title: 'Rank',
        key: 'rank',
      },
      {
        title: 'Stack',
        key: 'stack',
      },
      {
        title: 'Info',
        key: 'info',
      },
      {
        title: 'Date',
        key: 'date',
      },
      {
        title: 'Status',
        key: 'status',
      },
      {
        title: '',
        key: 'details',
      },
    ],
    []
  );



  const modifiedMatchedTalentList = useMemo(() => {
    return data?.data?.map((each) => [
      <div className='flex gap-[18px] items-center'>
        <div className='w-14 h-14'>
          <img
            src={each?.matchedTalentUser.photo ?? ""}
            alt={each?.matchedTalentUser.userName ?? ""}
          />
        </div>
        <div>
          <p>{each?.matchedTalentUser.firstName + " " + each?.matchedTalentUser.lastName}</p>
          {!!talentLevel(each) && (
            /* A job reaches its whole level band now, so one list can hold
               several levels. Quiet, because it is context not a headline. */
            <p className='text-[10px] text-muted-foreground'>{talentLevel(each)}</p>
          )}
            <p>
              {[each?.matchedTalentUser.userLocation?.city, each?.matchedTalentUser.userLocation?.state, each?.matchedTalentUser.userLocation?.country]
                .filter(Boolean).join(', ') || 'No location'}
            </p>
            {(each?.in_location === false || each?.in_location === 0) && (
              <span className='inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-800 border border-blue-200'>
                Outside job location
              </span>
            )}
            {roleBadge(each) && (
              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold border ${roleBadge(each).className}`}>
                {roleBadge(each).label}
              </span>
            )}
          {!!each?.matchedTalentUser.cv && <a target='_blank' href={each?.matchedTalentUser.cv} className='underline underline-offset-2 text-blue-600'>Resume</a>}
        </div>
      </div>,
      <MatchStars matched={each} />,
      // The role the talent is actually matched on lives in userCbtStack;
      // users.primaryStack is stale free text from an older signup.
      stackName(each.matchedTalentUser),
      <span className="capitalize">{each?.applied ? 'Applied' : 'Matched'}</span>,
      new Date(each?.created_at).toLocaleDateString(),
      <Status
        label={each?.status === 'approved' ? 'Approved' : each?.status === 'rejected' ? 'Rejected' : 'Pending'}
      />,
      <Link
        className='text-sm inline-block rounded-lg border border-border py-[1.56px] px-[11.7px] text-muted-foreground '
        to={`/matched-talents/cbt-profile/${each?.matchedTalentUser?.id}`}
      >
        Details
      </Link>
    ])
  })

  /* Poll the job until matching_info stops saying "Matching in progress"
     (the API sets that marker when it kicks the run off), then refresh. */
  const waitForMatching = useCallback(async () => {
    const started = Date.now();
    while (Date.now() - started < 60000) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const job = await fetchJob(jobId).unwrap();
        if (job?.matching_info !== 'Matching in progress') return;
      } catch (e) { /* keep polling */ }
    }
  }, [fetchJob, jobId]);

  const handleRematch = async () => {
    if (isSubmitting || isMatching) return;

    setIsSubmitting(true);

    rematchTalents({}, {
      onSuccess: async (res) => {
        setIsSubmitting(false);
        setIsMatching(true);
        await waitForMatching();
        setIsMatching(false);
        refetch();
        refetchTabs();
      },
      onError: (error) => {
        setIsSubmitting(false);
      },
    });
  };

  useEffect(() => {
    setTotalCount(data?.total ?? 0);
  }, [data]);

  return (
    <div className='w-full space-y-4'>
      <Button onClick={handleRematch} disabled={isSubmitting || isMatching} className='flex items-center space-x-2'>
        <p>{isMatching ? 'Matching…' : 'Rematch'}</p>
        {(isSubmitting || isMatching) && <ButtonSpinner />}
      </Button>

      {locationTabs?.gated && locationTabs.tabs?.length > 0 && (
        <div className='space-y-2'>
          <div role='tablist' aria-label='Talents by location' className='inline-flex max-w-full overflow-x-auto rounded-lg border p-1 text-xs'>
            {locationTabs.tabs.map((tab) => {
              const active = tab.key === ring;
              return (
                <button
                  key={tab.key}
                  type='button'
                  role='tab'
                  aria-selected={active}
                  onClick={() => { setRing(tab.key); setPage(0); }}
                  className={`whitespace-nowrap rounded-md px-3 py-1 font-semibold flex items-center gap-2 ${active ? 'bg-primary text-white' : 'text-muted-foreground'}`}
                >
                  <span>{tab.label}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${active ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'}`}>{tab.count}</span>
                </button>
              );
            })}
          </div>
          {/* This used to add "hidden from the client until they shortlist
              one", which was both a contradiction and untrue — the client's
              own location tabs read these rows with the visibility scope
              ignored. Saying nothing beats explaining a rule that does not
              hold. */}
          {ring !== 'inside' && (
            <p className='text-sm text-muted-foreground'>
              Qualified for the role but outside <strong>{locationTabs.location}</strong>.
            </p>
          )}
        </div>
      )}

      <Table
        header={tableHeader}
        label={ring === 'inside' ? 'List of matched Talents' : `Talents in ${locationTabs?.tabs?.find((t) => t.key === ring)?.label ?? ring}`}
        column={modifiedMatchedTalentList}
      />

      <section className="w-full">
        <Pagination
          rangeStart={
            totalCount !== 0
              ? (page || 0) * ((pageSize || 0) * pageColumn) + 1
              : 0
          }
          totalCount={totalCount}
          currentPage={(page || 0) + 1}
          pageSize={pageSize}
          pageColumn={pageColumn}
          siblingCount={siblingCount}
          onPageChange={handlePageChange}
          className="pt-9"
        />
      </section>
    </div>
  );
};

export default MatchedTalentsTable;
